import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  CheckBox as SelectAllIcon,
  CheckBoxOutlineBlank as DeselectAllIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { Grid } from '@mui/material';
import {
  useGetCommissionsQuery,
  useGetCommissionsSummaryQuery,
  useGetPendingCommissionsByEmployeeQuery,
  useMarkCommissionsAsPaidMutation,
} from '../services/commissions.service';
import { useGetUsersQuery } from '../services/users.service';

export default function CommissionsPage() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [isPaid, setIsPaid] = useState<boolean | ''>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [markAsPaidDialog, setMarkAsPaidDialog] = useState(false);
  const [viewCommissionDialog, setViewCommissionDialog] = useState(false);
  const [selectedCommissionForView, setSelectedCommissionForView] = useState<any>(null);

  // Helper function to safely format currency
  const formatCurrency = (value: any): string => {
    // Handle numbers
    if (typeof value === 'number' && !isNaN(value)) {
      return value.toFixed(2);
    }
    // Handle numeric strings (from Prisma Decimal fields)
    if (typeof value === 'string' && value.trim() !== '') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num.toFixed(2);
      }
    }
    return '0.00';
  };

  const {
    data: commissionsData,
    isLoading: commissionsLoading,
    error: commissionsError,
  } = useGetCommissionsQuery({
    page: page + 1,
    limit,
    userId: selectedUser || undefined,
    isPaid: isPaid !== '' ? isPaid : undefined,
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
  });

  const {
    data: summaryData,
    isLoading: summaryLoading,
  } = useGetCommissionsSummaryQuery({
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    userId: selectedUser || undefined,
  });

  const {
    data: pendingData,
    isLoading: pendingLoading,
  } = useGetPendingCommissionsByEmployeeQuery();

  const {
    data: usersData,
  } = useGetUsersQuery({
    limit: 1000,
    isActive: true,
  });

  const [markCommissionsAsPaid] = useMarkCommissionsAsPaidMutation();

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMarkAsPaid = async () => {
    if (selectedCommissions.length === 0) return;

    try {
      await markCommissionsAsPaid({ commissionIds: selectedCommissions }).unwrap();
      setSelectedCommissions([]);
      setMarkAsPaidDialog(false);
    } catch (error) {
      console.error('Error marking commissions as paid:', error);
    }
  };

  const handleSelectCommission = (commissionId: string) => {
    setSelectedCommissions(prev =>
      prev.includes(commissionId)
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  const handleSelectAll = () => {
    if (commissionsData?.commissions) {
      const unpaidCommissions = commissionsData.commissions
        .filter(commission => !commission.isPaid)
        .map(commission => commission.id);
      setSelectedCommissions(unpaidCommissions);
    }
  };

  const handleDeselectAll = () => {
    setSelectedCommissions([]);
  };

  const handleViewCommission = (commission: any) => {
    setSelectedCommissionForView(commission);
    setViewCommissionDialog(true);
  };

  const clearFilters = () => {
    setSelectedUser('');
    setIsPaid('');
    setStartDate(null);
    setEndDate(null);
  };

  if (commissionsError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Error cargando comisiones</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Sistema de Comisiones
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Total Comisiones
                      </Typography>
                      {summaryLoading ? (
                        <Skeleton width={80} height={32} />
                      ) : (
                        <Typography variant="h6">
                          L {formatCurrency(summaryData?.summary?.total?.amount)}
                        </Typography>
                      )}
                    </Box>
                    <TrendingUpIcon color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Pagadas
                      </Typography>
                      {summaryLoading ? (
                        <Skeleton width={80} height={32} />
                      ) : (
                        <Typography variant="h6" color="success.main">
                          L {formatCurrency(summaryData?.summary?.paid?.amount)}
                        </Typography>
                      )}
                    </Box>
                    <MoneyIcon color="success" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Pendientes
                      </Typography>
                      {summaryLoading ? (
                        <Skeleton width={80} height={32} />
                      ) : (
                        <Typography variant="h6" color="warning.main">
                          L {formatCurrency(summaryData?.summary?.pending?.amount)}
                        </Typography>
                      )}
                    </Box>
                    <ReceiptIcon color="warning" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Empleados con Comisiones
                      </Typography>
                      {pendingLoading ? (
                        <Skeleton width={40} height={32} />
                      ) : (
                        <Typography variant="h6">
                          {pendingData?.data.length || 0}
                        </Typography>
                      )}
                    </Box>
                    <PersonIcon color="info" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Filtros
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Empleado</InputLabel>
                  <Select
                    value={selectedUser}
                    label="Empleado"
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {usersData?.users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} {user.employeeCode && `(${user.employeeCode})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={isPaid === '' ? '' : isPaid ? 'true' : 'false'}
                    label="Estado"
                    onChange={(e) => {
                      const value = e.target.value as string;
                      setIsPaid(value === '' ? '' : value === 'true');
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="false">Pendientes</MenuItem>
                    <MenuItem value="true">Pagadas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <DatePicker
                  label="Fecha Inicio"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <DatePicker
                  label="Fecha Fin"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                  >
                    Limpiar Filtros
                  </Button>
                  
                  {/* Selection buttons */}
                  <Button
                    variant="outlined"
                    startIcon={<SelectAllIcon />}
                    onClick={handleSelectAll}
                    size="small"
                    disabled={commissionsLoading || !commissionsData?.commissions?.some(c => !c.isPaid)}
                  >
                    Seleccionar Todos
                  </Button>
                  
                  {selectedCommissions.length > 0 && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<DeselectAllIcon />}
                        onClick={handleDeselectAll}
                        size="small"
                        color="secondary"
                      >
                        Deseleccionar
                      </Button>
                      
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={() => setMarkAsPaidDialog(true)}
                        color="success"
                        size="small"
                      >
                        Marcar como Pagadas ({selectedCommissions.length})
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Commissions Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Empleado</TableCell>
                    <TableCell>Venta</TableCell>
                    <TableCell>Total Venta</TableCell>
                    <TableCell>Porcentaje</TableCell>
                    <TableCell>Comisión</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissionsLoading ? (
                    Array.from({ length: limit }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 9 }).map((_, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    commissionsData?.commissions.map((commission) => (
                      <TableRow key={commission.id}>
                        <TableCell padding="checkbox">
                          {!commission.isPaid && (
                            <input
                              type="checkbox"
                              checked={selectedCommissions.includes(commission.id)}
                              onChange={() => handleSelectCommission(commission.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {commission.user.name}
                            </Typography>
                            {commission.user.employeeCode && (
                              <Typography variant="caption" color="text.secondary">
                                Código: {commission.user.employeeCode}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              #{commission.sale.id.slice(-8)}
                            </Typography>
                            {commission.sale.customerName && (
                              <Typography variant="caption" color="text.secondary">
                                {commission.sale.customerName}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>L {formatCurrency(commission.saleTotal)}</TableCell>
                        <TableCell>{commission.percentage}%</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            L {formatCurrency(commission.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={commission.isPaid ? 'Pagada' : 'Pendiente'}
                            color={commission.isPaid ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {format(new Date(commission.createdAt), 'dd/MM/yyyy')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(commission.createdAt), 'HH:mm')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewCommission(commission)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={commissionsData?.pagination.total || 0}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </Paper>
        </Box>

        {/* Mark as Paid Dialog */}
        <Dialog
          open={markAsPaidDialog}
          onClose={() => setMarkAsPaidDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirmar Pago de Comisiones</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro de marcar {selectedCommissions.length} comisiones como pagadas?
              Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMarkAsPaidDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleMarkAsPaid}
              variant="contained"
              color="success"
            >
              Confirmar Pago
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Commission Dialog */}
        <Dialog
          open={viewCommissionDialog}
          onClose={() => setViewCommissionDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Detalles de la Comisión</DialogTitle>
          <DialogContent>
            {selectedCommissionForView && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        👤 Información del Empleado
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedCommissionForView.user.name}
                        </Typography>
                        {selectedCommissionForView.user.employeeCode && (
                          <Typography variant="body2" color="text.secondary">
                            Código: {selectedCommissionForView.user.employeeCode}
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                        💰 Detalles de la Comisión
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Porcentaje:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {selectedCommissionForView.percentage}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Monto de Comisión:</Typography>
                        <Typography variant="body2" fontWeight="medium" color="primary.main">
                          L {formatCurrency(selectedCommissionForView.amount)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Estado:</Typography>
                        <Chip
                          label={selectedCommissionForView.isPaid ? 'Pagada' : 'Pendiente'}
                          color={selectedCommissionForView.isPaid ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      {selectedCommissionForView.paidAt && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Fecha de Pago:</Typography>
                          <Typography variant="body2">
                            {format(new Date(selectedCommissionForView.paidAt), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Fecha de Creación:</Typography>
                        <Typography variant="body2">
                          {format(new Date(selectedCommissionForView.createdAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        🧾 Información de la Venta
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          ID de Venta
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          #{selectedCommissionForView.sale.id.slice(-8)}
                        </Typography>
                      </Box>
                      
                      {selectedCommissionForView.sale.customerName && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Cliente
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedCommissionForView.sale.customerName}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total de la Venta
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          L {formatCurrency(selectedCommissionForView.saleTotal)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Fecha de Venta
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {format(new Date(selectedCommissionForView.sale.createdAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Cálculo de Comisión
                        </Typography>
                        <Typography variant="body2">
                          L {formatCurrency(selectedCommissionForView.saleTotal)} × {selectedCommissionForView.percentage}% = 
                          <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {' '}L {formatCurrency(selectedCommissionForView.amount)}
                          </span>
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewCommissionDialog(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}